import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { StyleSheet, css } from 'aphrodite'
import React, { useCallback, useEffect, useRef, useState } from 'react'

interface DataTableProps<T extends object> {
  data: T[]
  // biome-ignore lint/suspicious/noExplicitAny: Table must accept heterogeneous column value types across pages
  columns: ColumnDef<T, any>[]
  renderSubRow?: (row: T) => React.ReactNode
  isReportMode?: boolean
  selectedItems?: Set<string>
  onItemToggle?: (itemId: string, checked: boolean) => void
  getItemId?: (row: T) => string
  // Optional row styling callback for status-based color coding
  getRowStyle?: (row: T) => React.CSSProperties | undefined
  // Optional pagination props (backward-compatible)
  page?: number
  pages?: number
  count?: number
  pageSize?: number
  pageSizeOptions?: number[]
  onPageChange?: (nextPage: number) => void
  onPageSizeChange?: (size: number) => void
  // Optional client-side column sorting via clickable headers (backward-compatible)
  enableSorting?: boolean
}

const Table = <T extends object>({
  data,
  columns,
  renderSubRow,
  isReportMode = false,
  selectedItems = new Set(),
  onItemToggle,
  getItemId,
  getRowStyle,
  page = 1,
  pages = 1,
  count,
  pageSize = 10,
  pageSizeOptions = [10, 20, 50],
  onPageChange,
  onPageSizeChange,
  enableSorting = false,
}: DataTableProps<T>) => {
  const [sorting, setSorting] = useState<SortingState>([])
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [canScrollLeft, setCanScrollLeft] = useState(false)

  const checkScrollability = useCallback(() => {
    const container = scrollContainerRef.current
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1)
    }
  }, [])

  const scrollTable = useCallback((direction: 'left' | 'right') => {
    const container = scrollContainerRef.current
    if (container) {
      const scrollAmount = 200
      const newScrollLeft =
        direction === 'left'
          ? container.scrollLeft - scrollAmount
          : container.scrollLeft + scrollAmount
      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      })
    }
  }, [])

  useEffect(() => {
    // Initial check and delayed recheck for layout stabilization
    checkScrollability()
    const timeoutId = setTimeout(checkScrollability, 100)
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', checkScrollability)
      window.addEventListener('resize', checkScrollability)
      return () => {
        clearTimeout(timeoutId)
        container.removeEventListener('scroll', checkScrollability)
        window.removeEventListener('resize', checkScrollability)
      }
    }
    return () => clearTimeout(timeoutId)
  }, [checkScrollability])

  const tableInstance = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    ...(enableSorting && {
      getSortedRowModel: getSortedRowModel(),
      state: { sorting },
      onSortingChange: setSorting,
    }),
  })
  const headerGroups = tableInstance.getHeaderGroups()
  const rows = tableInstance.getRowModel().rows

  const showPagination = Boolean(
    (pages && pages > 1) || onPageChange || onPageSizeChange,
  )
  const canPrev = page > 1
  const canNext = page < pages

  return (
    <div className={css(styles.wrapper)}>
      {canScrollLeft && (
        <>
          <div className={css(styles.scrollIndicatorLeft)} />
          <button
            type="button"
            className={css(styles.scrollArrowLeft)}
            onClick={() => scrollTable('left')}
            aria-label="Scroll left"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>Scroll left</title>
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="#6B7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </>
      )}
      {canScrollRight && (
        <>
          <div className={css(styles.scrollIndicatorRight)} />
          <button
            type="button"
            className={css(styles.scrollArrowRight)}
            onClick={() => scrollTable('right')}
            aria-label="Scroll right"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>Scroll right</title>
              <path
                d="M7.5 15L12.5 10L7.5 5"
                stroke="#6B7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </>
      )}
      <div ref={scrollContainerRef} className={css(styles.scrollContainer)}>
        <table className={css(styles.dataTable)}>
          <thead className={css(styles.header)}>
            {headerGroups.map((headerGroup) => (
              <tr key={headerGroup.id}>
                {isReportMode && (
                  <th className={css(styles.headerCell)}>
                    <input
                      type="checkbox"
                      checked={
                        data.length > 0 &&
                        data.every((row) =>
                          selectedItems.has(getItemId?.(row) || ''),
                        )
                      }
                      onChange={(e) => {
                        const checked = e.target.checked
                        for (const row of data) {
                          const itemId = getItemId?.(row)
                          if (itemId) {
                            onItemToggle?.(itemId, checked)
                          }
                        }
                      }}
                    />
                  </th>
                )}
                {headerGroup.headers.map((header) => {
                  const sortable = enableSorting && header.column.getCanSort()
                  const sortDirection = header.column.getIsSorted()
                  return (
                    <th
                      className={css(
                        styles.headerCell,
                        sortable && styles.sortableHeaderCell,
                      )}
                      key={header.id}
                      onClick={
                        sortable
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                      onKeyDown={
                        sortable
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                header.column.toggleSorting()
                              }
                            }
                          : undefined
                      }
                      tabIndex={sortable ? 0 : undefined}
                      aria-sort={
                        sortable
                          ? sortDirection === 'asc'
                            ? 'ascending'
                            : sortDirection === 'desc'
                              ? 'descending'
                              : 'none'
                          : undefined
                      }
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {sortable && (
                        <span className={css(styles.sortIndicator)}>
                          {sortDirection === 'asc' && (
                            <i className="fa-solid fa-arrow-up" />
                          )}
                          {sortDirection === 'desc' && (
                            <i className="fa-solid fa-arrow-down" />
                          )}
                          {!sortDirection && (
                            <i
                              className={`fa-solid fa-arrows-up-down ${css(styles.sortIdle)}`}
                            />
                          )}
                        </span>
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.map((row) => (
              <React.Fragment key={row.id}>
                <tr
                  className={css(styles.row)}
                  style={getRowStyle?.(row.original)}
                >
                  {isReportMode && (
                    <td className={css(styles.cell)}>
                      <input
                        type="checkbox"
                        checked={selectedItems.has(
                          getItemId?.(row.original) || '',
                        )}
                        onChange={(e) => {
                          const itemId = getItemId?.(row.original)
                          if (itemId) {
                            onItemToggle?.(itemId, e.target.checked)
                          }
                        }}
                      />
                    </td>
                  )}
                  {row.getVisibleCells().map((cell) => (
                    <td className={css(styles.cell)} key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
                {renderSubRow?.(row.original)}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className={css(styles.paginationBar)}>
          <div className={css(styles.pageSizeSection)}>
            <label
              className={css(styles.pageSizeLabel)}
              htmlFor="page-size-select"
            >
              Rows per page
            </label>
            <select
              id="page-size-select"
              className={css(styles.pageSizeSelect)}
              value={pageSize}
              onChange={(e) => {
                const next = Number(e.target.value)
                onPageSizeChange?.(next)
              }}
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div className={css(styles.pageInfo)}>
            {typeof count === 'number' && <span>{count} total</span>}
            <span className={css(styles.pageInfoSpacer)} />
            <span>
              Page {page} of {Math.max(pages, 1)}
            </span>
          </div>
          <div className={css(styles.pageControls)}>
            <button
              type="button"
              className={css(styles.pageButton, !canPrev && styles.disabled)}
              onClick={() => canPrev && onPageChange?.(page - 1)}
              disabled={!canPrev || !onPageChange}
            >
              Prev
            </button>
            <button
              type="button"
              className={css(styles.pageButton, !canNext && styles.disabled)}
              onClick={() => canNext && onPageChange?.(page + 1)}
              disabled={!canNext || !onPageChange}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Table

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    position: 'relative' as const,
  },
  scrollIndicatorLeft: {
    position: 'absolute' as const,
    left: 0,
    top: 0,
    bottom: 0,
    width: 40,
    background:
      'linear-gradient(to right, rgba(255,255,255,0.95), transparent)',
    pointerEvents: 'none' as const,
    zIndex: 1,
  },
  scrollIndicatorRight: {
    position: 'absolute' as const,
    right: 0,
    top: 0,
    bottom: 0,
    width: 40,
    background: 'linear-gradient(to left, rgba(255,255,255,0.95), transparent)',
    pointerEvents: 'none' as const,
    zIndex: 1,
  },
  scrollArrowLeft: {
    position: 'absolute' as const,
    left: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 2,
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#F3F4F6',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15)',
    },
  },
  scrollArrowRight: {
    position: 'absolute' as const,
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 2,
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#F3F4F6',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15)',
    },
  },
  scrollContainer: {
    width: '100%',
    overflowX: 'auto',
    border: 'none',
    borderBottom: 'none',
    outline: 'none',
    boxShadow: 'none',
    // Remove perceived border on horizontal scrollbar by styling track/thumb
    scrollbarWidth: 'thin',
    scrollbarColor: '#D1D5DB transparent',
    '::-webkit-scrollbar': {
      height: 8,
      background: 'transparent',
    },
    '::-webkit-scrollbar-track': {
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
    },
    '::-webkit-scrollbar-thumb': {
      backgroundColor: '#D1D5DB',
      borderRadius: 4,
      border: '2px solid transparent',
      backgroundClip: 'padding-box',
    },
  },
  dataTable: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 1100,
  },
  header: {
    background: '#F3F4F6',
    height: 45,
  },
  sortableHeaderCell: {
    cursor: 'pointer',
    userSelect: 'none',
    ':hover': {
      color: '#1F2937',
    },
  },
  sortIndicator: {
    marginLeft: 6,
    fontSize: 11,
  },
  sortIdle: {
    color: '#9CA3AF',
  },
  headerCell: {
    textAlign: 'left',
    paddingLeft: 12,
    fontWeight: 600,
    color: '#4B5563',
    textTransform: 'uppercase',
    paddingRight: 12,
    whiteSpace: 'nowrap',
    '@media (max-width: 1024px)': {
      paddingLeft: 8,
      paddingRight: 8,
      fontSize: 12,
      whiteSpace: 'normal',
    },
  },
  row: {
    borderBottom: '1px solid #E5E7EB',
  },
  cell: {
    paddingLeft: 12,
    height: 57,
    fontWeight: 500,
    paddingRight: 12,
    whiteSpace: 'nowrap',
    '@media (max-width: 1024px)': {
      paddingLeft: 8,
      paddingRight: 8,
      height: 48,
      fontSize: 13,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
  paginationBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    fontSize: 14,
    border: 'none',
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 8,
    },
  },
  pageSizeSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  pageSizeLabel: {
    color: '#6B7280',
    fontWeight: 500,
  },
  pageSizeSelect: {
    padding: '6px 10px',
    border: '1px solid #E5E7EB',
    borderRadius: 6,
    backgroundColor: '#FFFFFF',
  },
  pageInfo: {
    display: 'flex',
    alignItems: 'center',
    color: '#6B7280',
    fontWeight: 500,
  },
  pageInfoSpacer: {
    width: 12,
    display: 'inline-block',
  },
  pageControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    '@media (max-width: 1024px)': {
      justifyContent: 'space-between',
    },
  },
  pageButton: {
    padding: '6px 10px',
    border: '1px solid #E5E7EB',
    borderRadius: 6,
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
  },
  disabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
})
